/**
 * Grid Utilities - Snapping and viewport constraint calculations
 * Provides pure functions for coordinate transformations
 */

/**
 * Snap a single value to the nearest grid cell
 */
export function snapToGrid(value: number, gridSize: number): number {
  return Math.round(value / gridSize) * gridSize;
}

/**
 * Snap a 2D position to grid
 */
export function snapPositionToGrid(
  pos: { x: number; y: number },
  gridSize: number
): { x: number; y: number } {
  return {
    x: snapToGrid(pos.x, gridSize),
    y: snapToGrid(pos.y, gridSize),
  };
}

/**
 * Snap a 2D size to grid
 */
export function snapSizeToGrid(
  size: { width: number; height: number },
  gridSize: number
): { width: number; height: number } {
  return {
    width: Math.max(gridSize * 4, snapToGrid(size.width, gridSize)), // Min 4 grid cells
    height: Math.max(gridSize * 4, snapToGrid(size.height, gridSize)),
  };
}

/**
 * Constrain position to stay within viewport bounds
 */
export function constrainToViewport(
  pos: { x: number; y: number },
  size: { width: number; height: number },
  viewportWidth: number,
  viewportHeight: number
): { x: number; y: number } {
  return {
    x: Math.max(0, Math.min(pos.x, viewportWidth - size.width)),
    y: Math.max(0, Math.min(pos.y, viewportHeight - size.height)),
  };
}

/**
 * Convert a panel's stored (canvas-space) position to the on-screen position
 * it renders at, applying zoom scale and pan offset.
 */
export function panelToScreen(
  pos: { x: number; y: number },
  zoomScale: number,
  panOffset: { x: number; y: number } = { x: 0, y: 0 }
): { x: number; y: number } {
  return {
    x: pos.x * zoomScale + panOffset.x,
    y: pos.y * zoomScale + panOffset.y,
  };
}

/**
 * Inverse of panelToScreen: convert an on-screen position (e.g. the result of a
 * drag) back into canvas space. The pan offset MUST be removed before
 * unscaling — dropping it makes every drag re-add the offset, which teleports
 * the panel by panOffset on the first mouse move.
 */
export function screenToPanel(
  pos: { x: number; y: number },
  zoomScale: number,
  panOffset: { x: number; y: number } = { x: 0, y: 0 }
): { x: number; y: number } {
  return {
    x: (pos.x - panOffset.x) / zoomScale,
    y: (pos.y - panOffset.y) / zoomScale,
  };
}

/**
 * Check if two rectangles collide
 */
export function rectsCollide(
  rect1: { x: number; y: number; width: number; height: number },
  rect2: { x: number; y: number; width: number; height: number }
): boolean {
  return (
    rect1.x < rect2.x + rect2.width &&
    rect1.x + rect1.width > rect2.x &&
    rect1.y < rect2.y + rect2.height &&
    rect1.y + rect1.height > rect2.y
  );
}

/**
 * Get which resize handle is being dragged based on mouse position
 */
export type ResizeHandle = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw' | null;

export function getResizeHandle(
  mouseX: number,
  mouseY: number,
  panelRect: DOMRect,
  handleSize: number = 12
): ResizeHandle {
  const rect = panelRect;
  const relX = mouseX - rect.left;
  const relY = mouseY - rect.top;
  const width = rect.width;
  const height = rect.height;

  // Check corners (higher priority)
  if (relX < handleSize && relY < handleSize) return 'nw';
  if (relX > width - handleSize && relY < handleSize) return 'ne';
  if (relX > width - handleSize && relY > height - handleSize) return 'se';
  if (relX < handleSize && relY > height - handleSize) return 'sw';

  // Check edges
  if (relY < handleSize) return 'n';
  if (relY > height - handleSize) return 's';
  if (relX < handleSize) return 'w';
  if (relX > width - handleSize) return 'e';

  return null;
}

/**
 * Calculate new size based on resize handle and delta
 */
export function calculateNewSize(
  currentSize: { width: number; height: number },
  handle: ResizeHandle,
  deltaX: number,
  deltaY: number,
  minSize: { width: number; height: number } = { width: 300, height: 200 }
): { width: number; height: number } {
  let { width, height } = currentSize;

  if (handle?.includes('e')) width = Math.max(minSize.width, width + deltaX);
  if (handle?.includes('w')) width = Math.max(minSize.width, width - deltaX);
  if (handle?.includes('s')) height = Math.max(minSize.height, height + deltaY);
  if (handle?.includes('n')) height = Math.max(minSize.height, height - deltaY);

  return { width, height };
}

/**
 * Calculate new position when resizing from left/top edges
 */
export function calculateNewPosition(
  currentPos: { x: number; y: number },
  handle: ResizeHandle,
  deltaX: number,
  deltaY: number,
  _minSize: { width: number; height: number } = { width: 300, height: 200 }
): { x: number; y: number } {
  let { x, y } = currentPos;

  if (handle?.includes('w')) x += deltaX;
  if (handle?.includes('n')) y += deltaY;

  return { x, y };
}

/**
 * Get cursor style for resize handle
 */
export function getCursorForHandle(handle: ResizeHandle): string {
  if (handle === null) return 'default';
  const cursorMap: Record<Exclude<ResizeHandle, null>, string> = {
    n: 'ns-resize',
    ne: 'nesw-resize',
    e: 'ew-resize',
    se: 'nwse-resize',
    s: 'ns-resize',
    sw: 'nesw-resize',
    w: 'ew-resize',
    nw: 'nwse-resize',
  };
  return cursorMap[handle] || 'default';
}
